import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Radio, RadioGroup,
  FormControlLabel, Paper, Divider, Dialog,
  DialogContent
} from '@mui/material';
import axios from 'axios';
import Swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';

export default function ManagementTest({ user }) {
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [examStarted, setExamStarted] = useState(false);

  useEffect(() => {
    axios.get('https://filesregsiteration.sstli.com/get_icdl_questions2.php')
      .then(res => setQuestions(res.data))
      .catch(err => console.error(err));
  }, []);

  // فقط الأسئلة الاختيارية
  const mcqQuestions = questions.filter(q => !q.isPractical)

  useEffect(() => {
    if (!examStarted) return;
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = 120 * 60 - elapsed;
      if (remaining <= 0) {
        clearInterval(timer);
        handleSubmit();
        return;
      }
      setTimeLeft(remaining);
    }, 1000);
    return () => clearInterval(timer);
  }, [examStarted]);

  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });

  // تعليمات الإدارة
  const showInstructions = () => {
    Swal.fire({
      title: '📢 تعليمات هامة قبل بدء اختبار الإدارة',
      html: `
      <div style="text-align: right; line-height: 1.8; font-size: 16px;">
        ✅ مدة الاختبار: <b>120 دقيقة</b><br/>
        ✅ عدد الأسئلة: <b>66 سؤال</b><br/>
        ⚠️ سيتم تقييمك فقط على الأسئلة فورًا<br/>
        <br/>
      </div>
      `,
      icon: 'info',
      confirmButtonText: '🚀 ابدأ اختبار الإدارة الآن',
      confirmButtonColor: '#3085d6',
      width: 600
    }).then((result) => {
      if (result.isConfirmed) {
        setShowIntro(false);
        setExamStarted(true);
      }
    });
  };

  const [showIntro, setShowIntro] = useState(true);

  const handleSubmit = async () => {
    setIsLoading(true);
    // Check if the user has already submitted the exam
    try {
      const checkResponse = await axios.get('https://filesregsiteration.sstli.com/ExamResults2.php');
      const previousResults = checkResponse.data?.data || [];

      const isAlreadySubmitted = previousResults.some(
        (entry) => entry.UserName === loginData.username
      );

      if (isAlreadySubmitted) {
        Swal.fire({
          icon: 'warning',
          title: '⛔️ لا يمكن إعادة الاختبار',
          text: 'لقد قمت بتقديم هذا الاختبار من قبل.',
          confirmButtonText: 'حسنًا',
          confirmButtonColor: '#d33'
        });
        setIsLoading(false);
        return;
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'خطأ',
        text: 'حدث خطأ أثناء التحقق من بياناتك. حاول مرة أخرى.',
        confirmButtonText: 'حسنًا',
        confirmButtonColor: '#d33'
      });
      setIsLoading(false);
      return;
    }

    // حساب الدرجة
    const correctMcqAnswers = mcqQuestions.filter(q => answers[q.Id] === q.CorrectOption);
    const score = correctMcqAnswers.length;

    const formData = new FormData();
    formData.append("username", loginData.username);
    formData.append("score", score);

    try {
      const response = await axios.post('https://filesregsiteration.sstli.com/submit_exam2.php', formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.success) {
        Swal.fire({
          title: '✅ تم إنهاء اختبار الإدارة بنجاح',
          html: `
            <div style="text-align:right; font-size:16px;">
              🧮 حصلت على <b>${score} من ${mcqQuestions.length}</b>  
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'حسنًا',
          confirmButtonColor: '#3085d6'
        });
        setTimeout(() => {
          window.location.reload();
        }, 4000);
      } else {
        Swal.fire({
          icon: 'error',
          text: response.data.message,
          confirmButtonText: 'حسنًا',
          confirmButtonColor: '#d33'
        });
      }
    } catch (error) {
      Swal.fire({
        title: '✅ تم إنهاء الاختبار',
        html: `
          <div style="text-align:right; font-size:16px;">
            🧮 حصلت على <b>${score} من ${mcqQuestions.length}</b>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'حسنًا',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const formatTime = (t) => `${Math.floor(t / 60).toString().padStart(2, '0')}:${(t % 60).toString().padStart(2, '0')}`;

  // فقط الأسئلة الاختيارية (عن الإدارة)
  const renderQuestion = () => {
    const q = mcqQuestions[page];
    if (!q) return null;

    return (
      <Box>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 999,
          bgcolor: '#fff',
          py: 2,
          mb: 2,
          borderBottom: '2px solid #ddd'
        }}>
          <Typography style={{fontFamily:"cairo"}} variant="h5" fontWeight="bold">
            📝 اختبار الإدارة 
          </Typography>
          <Typography style={{fontFamily:"cairo"}} variant="subtitle1" color="text.secondary">
            سؤال {page + 1} من {mcqQuestions.length}
          </Typography>
        </Box>

        <Paper sx={{ p: 4, mb: 3 }}>
          <Typography style={{fontFamily:"cairo"}} variant="h6" mb={2}>{q.QuestionText}</Typography>
          <RadioGroup
            value={answers[q.Id] || ''}
            onChange={(e) => handleSelect(q.Id, e.target.value)}
          >
            <FormControlLabel style={{fontFamily:"cairo"}} value="A" control={<Radio />} label={q.OptionA} />
            <FormControlLabel style={{fontFamily:"cairo"}} value="B" control={<Radio />} label={q.OptionB} />
            <FormControlLabel style={{fontFamily:"cairo"}} value="C" control={<Radio />} label={q.OptionC} />
            <FormControlLabel style={{fontFamily:"cairo"}} value="D" control={<Radio />} label={q.OptionD} />
          </RadioGroup>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, direction: 'rtl', fontFamily: 'Cairo', width: "90%", margin: "auto" }}>
      {showIntro ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '85vh',
            background: 'linear-gradient(to bottom, #f0f4f8, #ffffff)',
            fontFamily: 'Cairo',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              textAlign: 'center',
              p: 5,
              maxWidth: 550,
              width: '90%',
              borderRadius: 4,
              border: '1px solid #ddd',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{ fontFamily: 'Cairo' }}
              >
                🧪 اختبار الإدارة
              </Typography>

              <Typography
                variant="h6"
                sx={{ mt: 2, color: '#444', fontFamily: 'Cairo' }}
              >
                اختبار في مهارات ومفاهيم الإدارة
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ mt: 1, color: '#666', fontSize: '16px', fontFamily: 'Cairo' }}
              >
                هذا الاختبار مقدم من <b>معهد السعودي المتخصص العالي للتدريب</b>
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 4, px: 5, py: 1.5, fontSize: '18px', fontFamily: 'Cairo' }}
                onClick={() => setLoginDialogOpen(true)}
              >
                🚀 ابدأ اختبار الإدارة
              </Button>
            </Box>

            <Box sx={{ mt: 6, pt: 2, borderTop: '1px solid #eee' }}>
              <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Cairo' }}>
                فريق الدعم الفني وتطوير البرمجيات      
              </Typography>
            </Box>
          </Paper>
        </Box>
      ) : (
        <>
          {/* رأس الصفحة */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" style={{fontFamily:"cairo"}}>🧪 اختبار الإدارة</Typography>
            <Box sx={{ bgcolor: '#ffe082', px: 2, py: 1, borderRadius: '8px', fontSize: '18px' }}>
              ⏱ {formatTime(timeLeft || 0)}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* السؤال الحالي */}
          {renderQuestion()}

          {/* أزرار التنقل */}
          <div style={{width:"90%",display:"flex",justifyContent:"space-between",margin:"auto"}}>
            <Button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              variant="outlined"
              style={{fontFamily:"cairo"}}
            >
              ⬅️ السابق
            </Button>
            <Button
              variant="contained"
              style={{fontFamily:"cairo"}}
              onClick={() => {
                if (page === mcqQuestions.length - 1) {
                  handleSubmit(false);
                } else {
                  setPage(p => p + 1);
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? 'جاري التحميل...' : (page === mcqQuestions.length - 1 ? '🧾 إنهاء الاختبار' : 'التالي ➡️')}
            </Button>
          </div>
        </>
      )}

      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent sx={{ direction: 'rtl', fontFamily: 'Cairo' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
            تسجيل الدخول لبدء اختبار الإدارة
          </Typography>

          <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              placeholder="اسم المستخدم"
              style={{
                width: '80%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                backgroundColor: '#f5faff',
                fontFamily: 'Cairo',
                fontSize: '16px',
              }}
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
            />

            <input
              type="password"
              placeholder="كلمة المرور"
              style={{
                width: '80%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                backgroundColor: '#f5faff',
                fontFamily: 'Cairo',
                fontSize: '16px',
              }}
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            />

            <Button
              variant="contained"
              fullWidth
              style={{ fontFamily: 'Cairo', fontSize: '16px', padding: '10px 0', borderRadius: '8px' }}
              onClick={async () => {
                if (!loginData.username || !loginData.password) {
                  Swal.fire({
                    icon: 'warning',
                    text: 'يرجى إدخال اسم المستخدم وكلمة المرور',
                    confirmButtonText: 'حسنًا',
                    confirmButtonColor: '#d33'
                  });
                  return;
                }

                try {
                  const res = await axios.post('https://filesregsiteration.sstli.com/login_apiSecond.php', {
                    username: loginData.username,
                    password: loginData.password
                  });

                  if (res.data.success) {
                    setLoginDialogOpen(false);
                    showInstructions();
                  } else {
                    Swal.fire({
                      icon: 'error',
                      title: 'خطأ',
                      text: res.data.message
                    });
                  }
                } catch (error) {
                  Swal.fire({
                    icon: 'error',
                    title: 'فشل التحقق',
                    text: error.response?.data?.message || 'حدث خطأ أثناء الاتصال بالخادم'
                  });
                }
              }}
            >
              تسجيل الدخول
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
